import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
  Select, MenuItem, InputLabel, FormControl, Chip, Typography,
  Box, CircularProgress, Switch, FormControlLabel, Stack,
} from '@mui/material';

const API = 'https://portal.peaksoftcon.com:8081/api/bot';

// Add new entries here when more teknokent scrapers come online (Phase 3+).
const SCRAPER_OPTIONS = [
  { key: 'hacettepe', label: 'Hacettepe Teknokent' },
];

const STATUS_COLORS = {
  queued: 'default',
  running: 'info',
  success: 'success',
  failed: 'error',
};

const blankForm = {
  id: null,
  name: '',
  scraperKey: 'hacettepe',
  username: '',
  password: '',
  notes: '',
  active: true,
};

function StatusChip({ status }) {
  if (!status) return <Chip size="small" label="—" />;
  return <Chip size="small" color={STATUS_COLORS[status] || 'default'} label={status} />;
}

export default function AdminBotPanel() {
  const [companies, setCompanies] = useState([]);
  const [latestRunByCompany, setLatestRunByCompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [isEditing, setIsEditing] = useState(false);
  const [savingForm, setSavingForm] = useState(false);
  const [runningIds, setRunningIds] = useState(new Set());
  const [historyCompany, setHistoryCompany] = useState(null);
  const [historyRuns, setHistoryRuns] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/companies`);
      setCompanies(data || []);
      // Pull the latest run per company in parallel so the table shows status
      // without forcing the user to open history.
      const pairs = await Promise.all(
        (data || []).map(async (c) => {
          try {
            const { data: runs } = await axios.get(`${API}/companies/${c.id}/runs`);
            return [c.id, (runs && runs[0]) || null];
          } catch {
            return [c.id, null];
          }
        }),
      );
      setLatestRunByCompany(Object.fromEntries(pairs));
    } catch (err) {
      console.error('failed to load companies', err);
      alert('Firmalar yüklenemedi: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const openCreate = () => {
    setIsEditing(false);
    setForm(blankForm);
    setFormOpen(true);
  };

  const openEdit = (company) => {
    setIsEditing(true);
    // username/password aren't returned by the bot (they're encrypted at rest)
    // — leave them blank; user may type new values to overwrite.
    setForm({
      id: company.id,
      name: company.name || '',
      scraperKey: company.scraperKey || 'hacettepe',
      username: '',
      password: '',
      notes: company.notes || '',
      active: company.active !== false,
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    if (savingForm) return;
    setFormOpen(false);
  };

  const onFormChange = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: val }));
  };

  const submitForm = async () => {
    if (!form.name.trim() || !form.scraperKey) {
      alert('Firma adı ve teknokent seçimi zorunlu.');
      return;
    }
    if (!isEditing && (!form.username || !form.password)) {
      alert('Yeni firma için kullanıcı adı ve şifre zorunlu.');
      return;
    }
    setSavingForm(true);
    try {
      if (isEditing) {
        const patch = {
          name: form.name,
          scraperKey: form.scraperKey,
          notes: form.notes,
          active: form.active,
        };
        if (form.username) patch.username = form.username;
        if (form.password) patch.password = form.password;
        await axios.put(`${API}/companies/${form.id}`, patch);
      } else {
        await axios.post(`${API}/companies`, {
          name: form.name,
          scraperKey: form.scraperKey,
          username: form.username,
          password: form.password,
          notes: form.notes,
          active: form.active,
        });
      }
      setFormOpen(false);
      await loadCompanies();
    } catch (err) {
      alert('Kaydetme hatası: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingForm(false);
    }
  };

  const removeCompany = async (company) => {
    if (!window.confirm(`"${company.name}" firmasını silmek istediğine emin misin? Şifreli kredentiyaller ve geçmiş run'lar da silinecek.`)) return;
    try {
      await axios.delete(`${API}/companies/${company.id}`);
      await loadCompanies();
    } catch (err) {
      alert('Silme hatası: ' + (err.response?.data?.error || err.message));
    }
  };

  const triggerRun = async (company) => {
    setRunningIds((s) => new Set(s).add(company.id));
    try {
      await axios.post(`${API}/companies/${company.id}/run`, {});
      // The bot run is async; refresh after a short delay to surface "running"
      // status, then poll a couple of times.
      setTimeout(() => loadCompanies(), 1500);
      setTimeout(() => loadCompanies(), 6000);
      setTimeout(() => loadCompanies(), 30000);
    } catch (err) {
      alert('Çalıştırma hatası: ' + (err.response?.data?.error || err.message));
    } finally {
      setTimeout(() => {
        setRunningIds((s) => {
          const next = new Set(s);
          next.delete(company.id);
          return next;
        });
      }, 1500);
    }
  };

  const openHistory = async (company) => {
    setHistoryCompany(company);
    setHistoryRuns([]);
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API}/companies/${company.id}/runs`);
      setHistoryRuns(data || []);
    } catch (err) {
      alert('Geçmiş yüklenemedi: ' + (err.response?.data?.error || err.message));
    } finally {
      setHistoryLoading(false);
    }
  };

  const downloadFile = async (run, file) => {
    // Fetch through the proxied /api/bot/runs/:runKey/files/:fileName, which
    // streams Excel back with proper Content-Disposition.
    try {
      const url = `${API}/runs/${encodeURIComponent(run.runKey)}/files/${encodeURIComponent(file.fileName)}`;
      const { data } = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([data]);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = file.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      alert('İndirme hatası: ' + (err.response?.data?.error || err.message));
    }
  };

  const openRunDetail = async (run) => {
    try {
      const { data } = await axios.get(`${API}/runs/${encodeURIComponent(run.runKey)}`);
      const filesInfo = (data.files || [])
        .map((f) => `${f.fileName}${f.sizeBytes ? ` (${Math.round(f.sizeBytes / 1024)}KB)` : ''}`)
        .join('\n');
      const summary = [
        `Run: ${data.runKey}`,
        `Status: ${data.status}`,
        `Started: ${data.startedAt || '-'}`,
        `Finished: ${data.finishedAt || '-'}`,
        data.errorMessage ? `\nHata:\n${data.errorMessage}` : '',
        filesInfo ? `\nDosyalar:\n${filesInfo}` : '',
      ].filter(Boolean).join('\n');
      // Quick-and-dirty detail view; replace with a proper dialog if we
      // start showing more fields per run.
      if (data.files && data.files.length > 0) {
        const yes = window.confirm(`${summary}\n\nİlk dosyayı indirmek için OK.`);
        if (yes) downloadFile(data, data.files[0]);
      } else {
        alert(summary);
      }
    } catch (err) {
      alert('Run detayı alınamadı: ' + (err.response?.data?.error || err.message));
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Typography variant="h5">Bot Yönetimi</Typography>
        <Button onClick={loadCompanies} disabled={loading} size="small" variant="outlined">
          {loading ? <CircularProgress size={16} /> : 'Yenile'}
        </Button>
        <Box flex={1} />
        <Button variant="contained" onClick={openCreate}>+ Yeni Firma</Button>
      </Box>

      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
        Şifreler AES-256-GCM ile master key altında tutulur. Master key sunucuda
        ayrı dosyada (botuser sahipliğinde 0400). Mevcut bir firmayı düzenlerken
        kullanıcı adı/şifre alanlarını boş bırakırsan değişmez.
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Firma</TableCell>
              <TableCell>Teknokent</TableCell>
              <TableCell>Aktif</TableCell>
              <TableCell>Son Run</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell align="right">İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Henüz firma eklenmemiş.
                </TableCell>
              </TableRow>
            )}
            {companies.map((c) => {
              const last = latestRunByCompany[c.id];
              const isRunning = runningIds.has(c.id) || last?.status === 'running';
              return (
                <TableRow key={c.id}>
                  <TableCell>{c.id}</TableCell>
                  <TableCell>
                    <strong>{c.name}</strong>
                    {c.notes && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {c.notes}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{c.scraperKey}</TableCell>
                  <TableCell>{c.active ? 'Evet' : 'Hayır'}</TableCell>
                  <TableCell>
                    {last ? new Date(last.startedAt || last.createdAt).toLocaleString('tr-TR') : '—'}
                  </TableCell>
                  <TableCell><StatusChip status={last?.status} /></TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => triggerRun(c)}
                        disabled={isRunning || !c.active}
                      >
                        {isRunning ? <CircularProgress size={14} color="inherit" /> : 'Çalıştır'}
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => openHistory(c)}>
                        Geçmiş
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => openEdit(c)}>
                        Düzenle
                      </Button>
                      <Button size="small" variant="outlined" color="error" onClick={() => removeCompany(c)}>
                        Sil
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit dialog */}
      <Dialog open={formOpen} onClose={closeForm} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Firmayı Düzenle' : 'Yeni Firma Ekle'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Firma Adı"
            value={form.name}
            onChange={onFormChange('name')}
            fullWidth
            margin="dense"
            required
          />
          <FormControl fullWidth margin="dense" required>
            <InputLabel id="bot-scraper-label">Teknokent</InputLabel>
            <Select
              labelId="bot-scraper-label"
              label="Teknokent"
              value={form.scraperKey}
              onChange={onFormChange('scraperKey')}
            >
              {SCRAPER_OPTIONS.map((s) => (
                <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={isEditing ? 'Kullanıcı Adı (boş bırak = değiştirme)' : 'Kullanıcı Adı'}
            value={form.username}
            onChange={onFormChange('username')}
            fullWidth
            margin="dense"
            required={!isEditing}
            autoComplete="off"
          />
          <TextField
            label={isEditing ? 'Şifre (boş bırak = değiştirme)' : 'Şifre'}
            type="password"
            value={form.password}
            onChange={onFormChange('password')}
            fullWidth
            margin="dense"
            required={!isEditing}
            autoComplete="new-password"
          />
          <TextField
            label="Notlar (opsiyonel)"
            value={form.notes}
            onChange={onFormChange('notes')}
            fullWidth
            margin="dense"
            multiline
            minRows={2}
          />
          <FormControlLabel
            control={<Switch checked={form.active} onChange={onFormChange('active')} />}
            label="Aktif (cron ile çalışır)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm} disabled={savingForm}>İptal</Button>
          <Button onClick={submitForm} variant="contained" disabled={savingForm}>
            {savingForm ? <CircularProgress size={18} /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Run history dialog */}
      <Dialog
        open={!!historyCompany}
        onClose={() => setHistoryCompany(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Run Geçmişi — {historyCompany?.name}
        </DialogTitle>
        <DialogContent>
          {historyLoading && <CircularProgress />}
          {!historyLoading && historyRuns.length === 0 && (
            <Typography color="text.secondary">Bu firma için henüz run yok.</Typography>
          )}
          {!historyLoading && historyRuns.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Başlangıç</TableCell>
                  <TableCell>Bitiş</TableCell>
                  <TableCell>Tetikleyici</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Hata / Detay</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyRuns.map((r) => (
                  <TableRow key={r.runKey}>
                    <TableCell>{r.startedAt ? new Date(r.startedAt).toLocaleString('tr-TR') : '—'}</TableCell>
                    <TableCell>{r.finishedAt ? new Date(r.finishedAt).toLocaleString('tr-TR') : '—'}</TableCell>
                    <TableCell>{r.triggerType}</TableCell>
                    <TableCell><StatusChip status={r.status} /></TableCell>
                    <TableCell style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.errorMessage || '—'}
                    </TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => openRunDetail(r)}>Detay</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryCompany(null)}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
